import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, LifeBuoy, Plus, X, Building2, Headphones } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { EmptyState } from '@/components/EmptyState';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  CATEGORIES,
  type Ticket,
  type TicketStatus,
  type TicketScope,
} from './support/supportCommon';
import { SelectField } from '@/components/ui/SelectField';

const TENANT_STATUS_FILTERS: TicketStatus[] = ['open', 'in_progress', 'on_hold', 'resolved', 'closed'];

// Active scope tab — drives the page (list filter + New Ticket destination).
type ActiveTab = 'tenant_internal' | 'platform';

const TAB_CONFIG: Record<ActiveTab, {
  label: string;
  description: string;
  icon: typeof Building2;
  emptyTitle: string;
  emptyDesc: string;
  newTicketLabel: string;
  toastSuccess: string;
}> = {
  tenant_internal: {
    label: 'My Team (Internal)',
    description: 'Queries between you and your team admin/manager.',
    icon: Building2,
    emptyTitle: 'No internal tickets',
    emptyDesc: 'Use “New Ticket” to raise an internal request to your team.',
    newTicketLabel: 'New Internal Ticket',
    toastSuccess: 'Sent to your team — response within 48 hours',
  },
  platform: {
    label: 'BrixLead Support',
    description: 'Product issues, integration help, billing — anything for BrixLead.',
    icon: Headphones,
    emptyTitle: 'No BrixLead Support tickets',
    emptyDesc: 'Use “New Ticket” to contact BrixLead support.',
    newTicketLabel: 'New BrixLead Ticket',
    toastSuccess: 'Sent to BrixLead Support — response within 48 hours',
  },
};

export function SupportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read active tab from URL (?tab=...) so navigation from detail page
  // returns the user to the same tab they came from. Default = internal.
  const tabFromUrl = searchParams.get('tab');
  const initialTab: ActiveTab = tabFromUrl === 'platform' ? 'platform' : 'tenant_internal';

  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const navigate = useNavigate();

  // Keep URL in sync with active tab so refresh / back / share preserves it.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      return next;
    }, { replace: true });
  }, [activeTab, setSearchParams]);

  // Fetch the active scope's tickets. Clear the list first so the user
  // never sees stale rows from the other tab while the new fetch is in
  // flight (race condition: tab A's slower response overwrites tab B's).
  // The `scope` param is the scope this fetch is FOR — we drop the
  // response if it doesn't match what's currently active when it lands.
  useEffect(() => {
    let cancelled = false;
    setTickets([]);          // clear stale rows immediately on tab switch
    setLoading(true);
    const scope = activeTab; // capture for this run
    const params: any = { scope };
    if (filterStatus) params.status = filterStatus;
    api.get('/support/tickets', { params })
      .then((res) => {
        if (cancelled || scope !== activeTab) return; // tab changed mid-flight
        setTickets(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err: any) => {
        if (cancelled) return;
        toast('error', err?.response?.data?.message || 'Failed to load tickets');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, filterStatus]);

  // Manual refresh used after creating a ticket etc.
  const fetchList = async () => {
    setLoading(true);
    try {
      const params: any = { scope: activeTab };
      if (filterStatus) params.status = filterStatus;
      const { data } = await api.get('/support/tickets', { params });
      setTickets(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const cfg = TAB_CONFIG[activeTab];
  // Defensive second pass — only render rows whose scope matches the
  // active tab. This guards against any cached or stale response that
  // somehow slips past the fetch-level cancellation above.
  const visibleTickets = tickets.filter((t) => t.scope === activeTab);
  const { sorted, sort, onSort } = useSort(visibleTickets);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-5">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-[#6366F1]" />
            Support
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Raise queries internally to your team, or contact BrixLead support for product issues.
          </p>
        </div>

        {/* Scope tabs — segmented control */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex mb-5 max-w-xl">
          {(['tenant_internal', 'platform'] as ActiveTab[]).map((s) => {
            const c = TAB_CONFIG[s];
            const Icon = c.icon;
            const active = activeTab === s;
            return (
              <button
                key={s}
                onClick={() => setActiveTab(s)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? 'bg-[#6366F1] text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <Icon className="h-4 w-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Action bar — current tab description + status filter + New Ticket */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <p className="text-xs text-slate-500">{cfg.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <SelectField
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]">
              <option value="">All Statuses</option>
              {TENANT_STATUS_FILTERS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </SelectField>
            {filterStatus && (
              <button onClick={() => setFilterStatus('')}
                className="h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-500 hover:text-red-500">
                Clear
              </button>
            )}
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold whitespace-nowrap">
              <Plus className="h-4 w-4" />
              {cfg.newTicketLabel}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">{cfg.label}</span>
            <span className="text-xs text-slate-400">
              {loading ? 'Loading…' : `${visibleTickets.length} ticket${visibleTickets.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
          ) : visibleTickets.length === 0 ? (
            <EmptyState icon={cfg.icon} title={cfg.emptyTitle} description={cfg.emptyDesc} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <TableHeadRow>
                    <SortTh label="S.No" sortable={false} />
                    <SortTh label="Ticket" sortKey="id" sort={sort} onSort={onSort} />
                    <SortTh label="Subject" sortKey="subject" sort={sort} onSort={onSort} />
                    <SortTh label="Category" sortKey="category" sort={sort} onSort={onSort} />
                    <SortTh label="Priority" sortKey="priority" sort={sort} onSort={onSort} />
                    <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                    <SortTh label="Last Update" sortKey="updatedAt" sort={sort} onSort={onSort} />
                  </TableHeadRow>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {sorted.map((t, i) => (
                    <tr
                      key={t.id}
                      className="hover:bg-gray-50/80 cursor-pointer"
                      onClick={() => navigate(`/support/${t.id}`)}>
                      <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">#{t.id.slice(-6).toUpperCase()}</td>
                      <td className="px-4 py-3 text-slate-800 max-w-[280px]">
                        <div className="truncate font-medium">{t.subject}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 capitalize whitespace-nowrap">
                        {CATEGORIES.find((c) => c.v === t.category)?.l ?? t.category}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded-full ${PRIORITY_COLORS[t.priority]}`}>
                          {t.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS_COLORS[t.status]}`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDateTime(t.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showNew && (
          <NewTicketModal
            scope={activeTab}
            successMessage={cfg.toastSuccess}
            onClose={() => setShowNew(false)}
            onSaved={() => { setShowNew(false); fetchList(); }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

// ── New ticket modal (scope is fixed by the active tab) ─────────────────────
interface AssignableUser { id: string; name: string; role: string }

function NewTicketModal({
  scope, successMessage, onClose, onSaved,
}: {
  scope: TicketScope; successMessage: string; onClose: () => void; onSaved: () => void;
}) {
  const { user } = useAuth();
  const [form, setForm] = useState<{ subject: string; message: string; category: string; priority: string; assignedTenantUserId: string; assignedTenantUserName: string }>({
    subject: '', message: '', category: 'general', priority: 'normal',
    assignedTenantUserId: '', assignedTenantUserName: '',
  });
  const [saving, setSaving] = useState(false);
  const [assignees, setAssignees] = useState<AssignableUser[]>([]);

  // For internal tickets, fetch the admin/manager users so the requester
  // can pre-assign the query to a specific teammate. Platform tickets
  // don't need this — BrixLead handles assignment on their side.
  useEffect(() => {
    if (scope !== 'tenant_internal') return;
    api.get('/users', { params: { limit: 100 } })
      .then((res) => {
        const list: AssignableUser[] = (res.data?.data ?? res.data?.users ?? res.data ?? [])
          .filter((u: any) => u.role === 'admin' || u.role === 'manager')
          .map((u: any) => ({ id: u.id, name: u.name, role: u.role }));
        setAssignees(list);
      })
      .catch(() => {});
  }, [scope]);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast('error', 'Subject and message are required');
      return;
    }
    setSaving(true);
    try {
      const body: any = { subject: form.subject, message: form.message, category: form.category, priority: form.priority, scope };
      if (scope === 'tenant_internal' && form.assignedTenantUserId) {
        body.assignedTenantUserId = form.assignedTenantUserId;
        body.assignedTenantUserName = form.assignedTenantUserName || null;
      }
      await api.post('/support/tickets', body);
      toast('success', successMessage);
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] placeholder:text-slate-400';
  const selectCls = `${inputCls} bg-white`;
  const labelCls = 'block text-xs font-medium text-slate-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-900">New Support Ticket</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Subject *</label>
            <input className={inputCls} placeholder="Short summary"
              value={form.subject}
              onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
              maxLength={200} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <SelectField className={selectCls} value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </SelectField>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <SelectField className={selectCls} value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
                {['low', 'normal', 'high', 'urgent'].map((v) =>
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                )}
              </SelectField>
            </div>
          </div>

          {/* Assignment — internal tickets only */}
          {scope === 'tenant_internal' && (
            <div>
              <label className={labelCls}>Assign to (optional)</label>
              <SelectField
                className={selectCls}
                value={form.assignedTenantUserId}
                onChange={(e) => {
                  const uid = e.target.value;
                  const uname = uid ? assignees.find((a) => a.id === uid)?.name ?? '' : '';
                  setForm((p) => ({ ...p, assignedTenantUserId: uid, assignedTenantUserName: uname }));
                }}>
                <option value="">— Unassigned —</option>
                {assignees.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.role}){a.id === user?.id ? ' — me' : ''}
                  </option>
                ))}
              </SelectField>
              <p className="text-[11px] text-slate-400 mt-1">
                Pick the team member who should handle this query. Admins/managers can also be reassigned later.
              </p>
            </div>
          )}

          <div>
            <label className={labelCls}>Message *</label>
            <textarea rows={6}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
              placeholder="Describe your issue"
              value={form.message}
              onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
              maxLength={10000} />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 border border-gray-200 hover:border-gray-300">Cancel</button>
          <button onClick={submit} disabled={saving || !form.subject.trim() || !form.message.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
}

export default SupportPage;
